"use client";

// import { useToast } from "@/context/ToastContext";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import type { FetchStaticParamsType } from "utils/serverActions/static/staticActions";
import { fetchStaticBatched } from "@/utils/queries/fetchStaticBatched";
// import { handleHookError } from "utils/helpers/handleError";

// Read through /api/q instead of as a Server Action, and the catalogs a screen asks
// for together travel in one request (see fetchStaticBatched).
const fetchStaticAction = (params: FetchStaticParamsType) => fetchStaticBatched(params);

export type StaticDataType = {
  value: number;
  label: string;
  code?: string; // Optional field for countries
  country_id?: number; // Optional field for states (their owning country)
};

export const queryKeys = {
  ratePeriod: (languageCode: string) => ["ratePeriod", languageCode] as const,
  paymentMode: (languageCode: string) => ["paymentMode", languageCode] as const,
  invoiceTimeMode: (languageCode: string) =>
    ["invoiceTimeMode", languageCode] as const,
  invoiceFrequency: (languageCode: string) =>
    ["invoiceFrequency", languageCode] as const,
  invoiceMode: (languageCode: string) => ["invoiceMode", languageCode] as const,
  gender: (languageCode: string) => ["gender", languageCode] as const,
  genderDualOnly: (languageCode: string) =>
    ["gender_dual_only", languageCode] as const,
  interviewStatus: (languageCode: string) =>
    ["interviewStatus", languageCode] as const,
  countries: (languageCode: string) => ["countries", languageCode] as const,
  shiftCycleType: (languageCode: string) =>
    ["shiftCycleType", languageCode] as const,
  states: (languageCode: string, countryId: number) =>
    ["states", languageCode, countryId] as const,
  bankMovementStatus: (languageCode: string) =>
    ["bankMovementStatus", languageCode] as const,
  bankTransactionType: (languageCode: string) =>
    ["bankTransactionType", languageCode] as const,
  bankOriginType: (languageCode: string) =>
    ["bankOriginType", languageCode] as const,
  assistanceLabels: (languageCode: string) =>
    ["assistanceLabels", languageCode] as const,
  assistanceQuestions: (languageCode: string) =>
    ["assistanceQuestions", languageCode] as const,
  timezones: () => ["timezones"] as const,
};

const FALLBACK_TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Mexico_City",
  "America/Bogota",
  "America/Lima",
  "America/Costa_Rica",
  "America/Guatemala",
  "America/Panama",
  "America/Santo_Domingo",
  "America/Sao_Paulo",
  "America/Argentina/Buenos_Aires",
  "America/Santiago",
  "Europe/London",
  "Europe/Madrid",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Dubai",
  "Australia/Sydney",
];

export type TimezoneOption = { value: string; label: string };

/**
 * Hook to get the rate period static data
 * @param languageCode - The language code to get the static data for
 * @returns The static data for the rate period
 */
export const useRatePeriodStatic = (languageCode: string) => {
  // const { showToast } = useToast();

  return useQuery({
    queryKey: queryKeys.ratePeriod(languageCode),
    queryFn: async () => {
      const data = await fetchStaticAction({
        p_language_code: languageCode,
        p_sections: ["rate_periods"],
      });
      // console.log("data in hook", data.rate_periods);
      return data.rate_periods || [];
    },
    onSuccess: () => {},
    // onError: (error: Error) => {
    //   handleHookError(
    //     showToast,
    //     error,
    //     "Failed to load contract details static data. Please try again."
    //   );
    // },
    staleTime: Infinity, // Static data never becomes stale
    cacheTime: 1000 * 60 * 60, // Cache for 1 hour
  } as UseQueryOptions<StaticDataType[]>);
};

/**
 * Hook to get the payment mode static data
 * @param languageCode - The language code to get the static data for
 * @returns The static data for the payment mode
 */
export const usePaymentModeStatic = (languageCode: string) => {
  // const { showToast } = useToast();

  return useQuery({
    queryKey: queryKeys.paymentMode(languageCode),
    queryFn: async () => {
      const data = await fetchStaticAction({
        p_language_code: languageCode,
        p_sections: ["payment_modes"],
      });
      return data.payment_modes || [];
    },
    onSuccess: () => {},
    // onError: (error: Error) => {
    //   handleHookError(
    //     showToast,
    //     error,
    //     "Failed to load contract details static data. Please try again."
    //   );
    // },
    staleTime: Infinity, // Static data never becomes stale
    cacheTime: 1000 * 60 * 60, // Cache for 1 hour
  } as UseQueryOptions<StaticDataType[]>);
};

/**
 * Hook to get the invoice time static data
 * @param languageCode - The language code to get the static data for
 * @returns The static data for the invoice time
 */
export const useInvoiceTimeModeStatic = (languageCode: string) => {
  // const { showToast } = useToast();

  return useQuery({
    queryKey: queryKeys.invoiceTimeMode(languageCode),
    queryFn: async () => {
      const data = await fetchStaticAction({
        p_language_code: languageCode,
        p_sections: ["invoice_time_mode"],
      });
      return data.invoice_time_mode || [];
    },
    onSuccess: () => {},
    // onError: (error: Error) => {
    //   handleHookError(
    //     showToast,
    //     error,
    //     "Failed to load contract details static data. Please try again."
    //   );
    // },
    staleTime: Infinity, // Static data never becomes stale
    cacheTime: 1000 * 60 * 60, // Cache for 1 hour
  } as UseQueryOptions<StaticDataType[]>);
};

/**
 * Hook to get the invoice frequency static data
 * @param languageCode - The language code to get the static data for
 * @returns The static data for the invoice frequency
 */
export const useInvoiceFrequencyStatic = (languageCode: string) => {
  // const { showToast } = useToast();

  return useQuery({
    queryKey: queryKeys.invoiceFrequency(languageCode),
    queryFn: async () => {
      const data = await fetchStaticAction({
        p_language_code: languageCode,
        p_sections: ["invoice_frequency"],
      });
      return data.invoice_frequency || [];
    },
    staleTime: Infinity, // Static data never becomes stale
    cacheTime: 1000 * 60 * 60, // Cache for 1 hour
  } as UseQueryOptions<StaticDataType[]>);
};

/**
 * Hook to get the invoice mode static data
 * @param languageCode - The language code to get the static data for
 * @returns The static data for the invoice mode
 */
export const useInvoiceModeStatic = (languageCode: string) => {
  // const { showToast } = useToast();

  return useQuery({
    queryKey: queryKeys.invoiceMode(languageCode),
    queryFn: async () => {
      const data = await fetchStaticAction({
        p_language_code: languageCode,
        p_sections: ["invoice_mode"],
      });
      return data.invoice_mode || [];
    },
    staleTime: Infinity, // Static data never becomes stale
    cacheTime: 1000 * 60 * 60, // Cache for 1 hour
  } as UseQueryOptions<StaticDataType[]>);
};

/**
 * Hook to get the gender static data
 * @param languageCode - The language code to get the static data for
 * @returns The static data for the gender
 */
export const useGenderStatic = (languageCode: string) => {
  // const { showToast } = useToast();

  return useQuery({
    queryKey: queryKeys.gender(languageCode),
    queryFn: async () => {
      const data = await fetchStaticAction({
        p_language_code: languageCode,
        p_sections: ["gender"],
      });
      return data.gender || [];
    },
    staleTime: Infinity, // Static data never becomes stale
    cacheTime: 1000 * 60 * 60, // Cache for 1 hour
  } as UseQueryOptions<StaticDataType[]>);
};

/**
 * Hook to get the gender static data
 * @param languageCode - The language code to get the static data for
 * @returns The static data for the gender
 */
export const useGenderDualOnlyStatic = (languageCode: string) => {
  // const { showToast } = useToast();

  return useQuery({
    queryKey: queryKeys.genderDualOnly(languageCode),
    queryFn: async () => {
      const data = await fetchStaticAction({
        p_language_code: languageCode,
        p_sections: ["gender_dual_only"],
      });
      return data.gender_dual_only || [];
    },
    staleTime: Infinity, // Static data never becomes stale
    cacheTime: 1000 * 60 * 60, // Cache for 1 hour
  } as UseQueryOptions<StaticDataType[]>);
};

/**
 * Hook to get the interview status static data
 * @param languageCode - The language code to get the static data for
 * @returns The static data for the interview status
 */
export const useInterviewStatusStatic = (languageCode: string) => {
  // const { showToast } = useToast();

  return useQuery({
    queryKey: queryKeys.interviewStatus(languageCode),
    queryFn: async () => {
      const data = await fetchStaticAction({
        p_language_code: languageCode,
        p_sections: ["interview_status"],
      });
      return data.interview_status || [];
    },
    staleTime: Infinity, // Static data never becomes stale
    cacheTime: 1000 * 60 * 60, // Cache for 1 hour
  } as UseQueryOptions<StaticDataType[]>);
};

/**
 * Hook to get the countries static data
 * @param languageCode - The language code to get the static data for
 * @returns The static data for the countries
 */
export const useCountriesStatic = (languageCode: string) => {
  // const { showToast } = useToast();

  return useQuery({
    queryKey: queryKeys.countries(languageCode),
    queryFn: async () => {
      const data = await fetchStaticAction({
        p_language_code: languageCode,
        p_sections: ["countries"],
      });
      return data.countries || [];
    },
    staleTime: Infinity, // Static data never becomes stale
    cacheTime: 1000 * 60 * 60, // Cache for 1 hour
  } as UseQueryOptions<StaticDataType[]>);
};

/**
 * Hook to get the list of IANA timezones supported by the runtime
 * @returns The static data for timezones (value and label are both the IANA identifier)
 */
export const useTimezonesStatic = () => {
  return useQuery({
    queryKey: queryKeys.timezones(),
    queryFn: async () => {
      const zones =
        typeof Intl.supportedValuesOf === "function"
          ? Intl.supportedValuesOf("timeZone")
          : FALLBACK_TIMEZONES;

      return zones
        .map((tz) => ({ value: tz, label: tz }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    staleTime: Infinity, // Static data never becomes stale
    cacheTime: 1000 * 60 * 60, // Cache for 1 hour
  } as UseQueryOptions<TimezoneOption[]>);
};

/**
 * Hook to get the shift cycle types static data
 * @param languageCode - The language code to get the static data for
 * @returns The static data for the shift cycle types
 */
export const useShiftCycleTypeStatic = (languageCode: string) => {
  // const { showToast } = useToast();

  return useQuery({
    queryKey: queryKeys.shiftCycleType(languageCode),
    queryFn: async () => {
      const data = await fetchStaticAction({
        p_language_code: languageCode,
        p_sections: ["shifts_cycle_type"],
      });
      console.log(
        "useShiftCycleTypeStatic - Raw data from fetchStaticAction:",
        data,
      );
      const result = data?.shifts_cycle_type || [];
      console.log("useShiftCycleTypeStatic - Processed result:", result);
      return result;
    },
    staleTime: Infinity, // Static data never becomes stale
    cacheTime: 1000 * 60 * 60, // Cache for 1 hour
  } as UseQueryOptions<StaticDataType[]>);
};

/**
 * Hook to get the states static data
 * @param languageCode - The language code to get the static data for
 * @param countryId - The country ID to get the static data for
 * @param options - The options to pass to the query
 * @returns The static data for the states
 */
export const useStatesStatic = (
  languageCode: string,
  countryId: number,
  options?: Omit<UseQueryOptions<StaticDataType[]>, "queryKey" | "queryFn">,
) => {
  return useQuery({
    queryKey: queryKeys.states(languageCode, countryId),
    queryFn: async () => {
      const data = await fetchStaticAction({
        p_language_code: languageCode,
        p_sections: ["states"],
        p_country_id: countryId,
      });
      return data.states || []; // ✅ Retorna un arreglo
    },
    ...options,
  });
};

/**
 * Hook to get the bank movement status static data
 * @param languageCode - The language code to get the static data for
 * @returns The static data for the bank movement status
 */
export const useBankMovementStatusStatic = (languageCode: string) => {
  return useQuery({
    queryKey: queryKeys.bankMovementStatus(languageCode),
    queryFn: async () => {
      const data = await fetchStaticAction({
        p_language_code: languageCode,
        p_sections: ["bank_movement_status"],
      });
      return data.bank_movement_status || [];
    },
  } as UseQueryOptions<StaticDataType[]>);
};

/**
 * Hook to get the bank movement status static data
 * @param languageCode - The language code to get the static data for
 * @returns The static data for the bank movement status
 */
export const useBankOriginTypeStatic = (languageCode: string) => {
  return useQuery({
    queryKey: queryKeys.bankOriginType(languageCode),
    queryFn: async () => {
      const data = await fetchStaticAction({
        p_language_code: languageCode,
        p_sections: ["bank_origin_type"],
      });
      return data.bank_origin_type || [];
    },
    staleTime: Infinity, // Data will never become stale
    cacheTime: 1000 * 60 * 60, // Cache for 1 hour
  } as UseQueryOptions<StaticDataType[]>);
};

/**
 * Hook to get the bank transaction type static data
 * @param languageCode - The language code to get the static data for
 * @returns The static data for the bank transaction type
 */
export const useBankTransactionTypeStatic = (languageCode: string) => {
  return useQuery({
    queryKey: queryKeys.bankTransactionType(languageCode),
    queryFn: async () => {
      const data = await fetchStaticAction({
        p_language_code: languageCode,
        p_sections: ["bank_transaction_type"],
      });
      return data.bank_transaction_type || [];
    },
    staleTime: Infinity,
    cacheTime: 1000 * 60 * 60,
  } as UseQueryOptions<StaticDataType[]>);
};

/**
 * Hook to get the bank movement status static data
 * @param languageCode - The language code to get the static data for
 * @returns The static data for the bank movement status
 */
export const useAssistanceLabelsStatic = (languageCode: string) => {
  return useQuery({
    queryKey: queryKeys.assistanceLabels(languageCode),
    queryFn: async () => {
      const data = await fetchStaticAction({
        p_language_code: languageCode,
        p_sections: ["assistance_labels"],
      });
      return data.assistance_labels || [];
    },
    staleTime: Infinity, // Static data never becomes stale
    cacheTime: 1000 * 60 * 60, // Cache for 1 hour
  } as UseQueryOptions<StaticDataType[]>);
};

/**
 * Hook to get the bank movement status static data
 * @param languageCode - The language code to get the static data for
 * @returns The static data for the bank movement status
 */
export const useAssistanceQuestionsStatic = (languageCode: string) => {
  return useQuery({
    queryKey: queryKeys.assistanceQuestions(languageCode),
    queryFn: async () => {
      const data = await fetchStaticAction({
        p_language_code: languageCode,
        p_sections: ["assistance_questions"],
      });
      return data.assistance_questions || [];
    },
    staleTime: Infinity, // Static data never becomes stale
    cacheTime: 1000 * 60 * 60, // Cache for 1 hour
  } as UseQueryOptions<StaticDataType[]>);
};



